import { useCallback, useEffect, useState } from 'react'
import {
    RiEyeLine,
    RiEyeOffLine,
    RiDeleteBinLine,
    RiArrowUpLine,
    RiArrowDownLine,
} from 'react-icons/ri'
import { getCanvasInstance } from '@/lib/canvasInstance'
import { useEditorStore } from '@/stores/useEditorStore'
import styles from './LayerPanel.module.css'

interface LayerItem {
    id: string
    type: string
    name: string
    visible: boolean
}

export function LayerPanel() {
    const { selectedObjectIds, pushUndo } = useEditorStore()
    const [layers, setLayers] = useState<LayerItem[]>([])

    const refreshLayers = useCallback(() => {
        const canvas = getCanvasInstance()
        if (!canvas) return

        const objects = canvas.getObjects()
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const items: LayerItem[] = objects.map((obj: any, i: number) => ({
            id: obj.id || String(i),
            type: obj.type || 'object',
            name: obj.type === 'i-text' || obj.type === 'text' || obj.type === 'textbox'
                ? (obj.text?.substring(0, 20) || 'Texto')
                : obj.type === 'image'
                    ? 'Imagen'
                    : obj.type?.charAt(0).toUpperCase() + obj.type?.slice(1) || `Objeto ${i + 1}`,
            visible: obj.visible !== false,
        })).reverse() // Top layer first

        setLayers(items)
    }, [])

    // Refresh on selection changes or render
    useEffect(() => {
        refreshLayers()
        const canvas = getCanvasInstance()
        if (!canvas) return

        const handler = () => refreshLayers()
        canvas.on('object:added', handler)
        canvas.on('object:removed', handler)
        canvas.on('object:modified', handler)
        return () => {
            canvas.off('object:added', handler)
            canvas.off('object:removed', handler)
            canvas.off('object:modified', handler)
        }
    }, [refreshLayers, selectedObjectIds])

    const toggleVisibility = useCallback((index: number) => {
        const canvas = getCanvasInstance()
        if (!canvas) return
        const objects = canvas.getObjects()
        const realIndex = objects.length - 1 - index
        const obj = objects[realIndex]
        if (obj) {
            obj.visible = !obj.visible
            canvas.requestRenderAll()
            refreshLayers()
        }
    }, [refreshLayers])

    const deleteLayer = useCallback((index: number) => {
        const canvas = getCanvasInstance()
        if (!canvas) return
        const objects = canvas.getObjects()
        const realIndex = objects.length - 1 - index
        const obj = objects[realIndex]
        if (obj) {
            pushUndo(JSON.stringify(canvas.toJSON()))
            canvas.remove(obj)
            canvas.requestRenderAll()
            refreshLayers()
        }
    }, [pushUndo, refreshLayers])

    const moveLayer = useCallback((index: number, direction: 'up' | 'down') => {
        const canvas = getCanvasInstance()
        if (!canvas) return
        const objects = canvas.getObjects()
        const realIndex = objects.length - 1 - index
        const obj = objects[realIndex]
        if (!obj) return

        pushUndo(JSON.stringify(canvas.toJSON()))
        if (direction === 'up') {
            canvas.bringObjectForward(obj)
        } else {
            canvas.sendObjectBackwards(obj)
        }
        canvas.requestRenderAll()
        refreshLayers()
    }, [pushUndo, refreshLayers])

    const selectLayer = useCallback((index: number) => {
        const canvas = getCanvasInstance()
        if (!canvas) return
        const objects = canvas.getObjects()
        const realIndex = objects.length - 1 - index
        const obj = objects[realIndex]
        if (obj) {
            canvas.setActiveObject(obj)
            canvas.requestRenderAll()
        }
    }, [])

    return (
        <div className={styles.panel}>
            <h3 className={styles.title}>Capas</h3>
            {layers.length === 0 ? (
                <p className={styles.empty}>Agrega elementos al lienzo</p>
            ) : (
                <div className={styles.layerList}>
                    {layers.map((layer, i) => (
                        <div
                            key={layer.id + i}
                            className={`${styles.layerItem} ${selectedObjectIds.includes(layer.id) ? styles.selected : ''}`}
                            onClick={() => selectLayer(i)}
                        >
                            <span className={styles.layerType}>{layer.type}</span>
                            <span className={styles.layerName}>{layer.name}</span>
                            <div className={styles.layerActions}>
                                <button
                                    className={styles.layerAction}
                                    onClick={(e) => { e.stopPropagation(); moveLayer(i, 'up') }}
                                    title="Subir"
                                >
                                    <RiArrowUpLine />
                                </button>
                                <button
                                    className={styles.layerAction}
                                    onClick={(e) => { e.stopPropagation(); moveLayer(i, 'down') }}
                                    title="Bajar"
                                >
                                    <RiArrowDownLine />
                                </button>
                                <button
                                    className={styles.layerAction}
                                    onClick={(e) => { e.stopPropagation(); toggleVisibility(i) }}
                                    title={layer.visible ? 'Ocultar' : 'Mostrar'}
                                >
                                    {layer.visible ? <RiEyeLine /> : <RiEyeOffLine />}
                                </button>
                                <button
                                    className={`${styles.layerAction} ${styles.deleteAction}`}
                                    onClick={(e) => { e.stopPropagation(); deleteLayer(i) }}
                                    title="Eliminar"
                                >
                                    <RiDeleteBinLine />
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    )
}
